import { useEffect, useState } from "react";

// Wide enough for a 334 px desktop cover at 2× DPR.
const RENDER_WIDTH_PX = 720;

/**
 * The first page of a congress PDF, drawn to a PNG object URL: the cover of a
 * document whose partner uploaded no thumbnail. pdf.js loads only the bytes of
 * that page when the storage answers range requests.
 */
async function renderPdfFirstPage(url: string): Promise<string> {
  // The legacy build: the modern one needs Chrome 125 / Safari 18, and the
  // native shells run older system webviews.
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
    import.meta.url
  ).toString();
  const loadingTask = pdfjs.getDocument({ url, disableAutoFetch: true });
  try {
    const document_ = await loadingTask.promise;
    const page = await document_.getPage(1);
    const viewport = page.getViewport({
      scale: RENDER_WIDTH_PX / page.getViewport({ scale: 1 }).width,
    });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const context = canvas.getContext("2d");
    if (context === null)
      throw new Error("should not happen: a new canvas has a 2d context");
    await page.render({ canvasContext: context, canvas, viewport }).promise;
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result === null) {
          reject(new Error(`The first page of ${url} did not encode`));
          return;
        }
        resolve(result);
      }, "image/png");
    });
    return URL.createObjectURL(blob);
  } finally {
    // Destroying the loading task also destroys its document.
    void loadingTask.destroy();
  }
}

// One render per PDF for the session: the space page draws a document in its
// grid and in its rail. A failed render stays failed (the icon stays).
const firstPages = new Map<string, Promise<string>>();

function firstPageOf(pdfUrl: string): Promise<string> {
  const cached = firstPages.get(pdfUrl);
  if (cached !== undefined) return cached;
  const rendering = renderPdfFirstPage(pdfUrl);
  firstPages.set(pdfUrl, rendering);
  return rendering;
}

/**
 * The first page of `pdfUrl` as an image URL. `null` while it renders, when it
 * failed, or without a PDF: the card then keeps its document icon.
 */
export function usePdfFirstPage(pdfUrl: string | null): string | null {
  const [rendered, setRendered] = useState<{
    pdfUrl: string;
    imageUrl: string;
  } | null>(null);

  useEffect(() => {
    if (pdfUrl === null) return;
    let cancelled = false;
    firstPageOf(pdfUrl).then(
      (imageUrl) => {
        if (!cancelled) setRendered({ pdfUrl, imageUrl });
      },
      (error: unknown) => {
        console.error("[pdfFirstPage] The first page did not render", error);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // A render of a previous URL is not this document's cover.
  return rendered === null || rendered.pdfUrl !== pdfUrl
    ? null
    : rendered.imageUrl;
}
